type SectionStatus = 'complete' | 'in-progress' | 'not-started';

type NarrativeOptionId = 'option-1' | 'option-2' | 'option-3';

interface NarrativeOption {
  id: NarrativeOptionId;
  num: string;
  title: string;
  desc: string;
}

interface OutlineSection {
  title: string;
  bullets: string[];
}

interface ParagraphMeta {
  id: string;
  num: number;
  title: string;
  hasChart: boolean;
}

interface SectionMeta {
  id: string;
  title: string;
  index: number;
  description: string;
  intro: string;
  status: SectionStatus;
  paragraphs: ParagraphMeta[];
}

interface SubgroupRates {
  white: number;
  hispanic_latino: number;
  black: number;
  iep: number;
  multilingual_learners: number;
  economically_disadvantaged: number;
  non_economically_disadvantaged: number;
}

interface Prototype2Facts {
  district: { name: string; cohort: string; year: string };
  cohort_size: number;
  postsecondary_readiness: {
    overall_pct: number;
    by_route: { college_only: number; career_only: number; both: number };
    lagging_subgroups: { iep: number; black: number; hispanic_latino: number; multilingual_learners: number };
  };
  graduation: {
    overall_pct: number;
    by_subgroup: {
      economically_disadvantaged: number;
      non_economically_disadvantaged: number;
      hispanic_latino: number;
      white: number;
    };
    gap_pp: { economic: number; hispanic_white: number };
  };
  college_eligibility: {
    overall_pct: number;
    by_subgroup: SubgroupRates;
  };
  cte: {
    career_prepared_only_pct: number;
  };
}

const NARRATIVE_OPTIONS: NarrativeOption[] = [
  {
    id: 'option-1',
    num: '1',
    title: 'Equity Progress with Persistent Gaps',
    desc: 'Lead with the headline that 76% of students are postsecondary ready — a strong overall result — but foreground the fact that four student groups still lag significantly behind, framing the report around where targeted intervention is most needed.',
  },
  {
    id: 'option-2',
    num: '2',
    title: 'College Readiness as the Linchpin',
    desc: "Center the narrative around college readiness as the most predictive factor for long-term student success, using XQ's three-measure framework to show where Issaquah excels (coursework intensity) and where it falls short (GPA equity).",
  },
  {
    id: 'option-3',
    num: '3',
    title: 'CTE Pathways as an Equity Lever',
    desc: 'Highlight Career & Technical Education as a bright spot — 25% of students met career-prepared criteria exclusively, suggesting CTE pathways are reaching students who might not meet traditional college-ready benchmarks, and argue for expanding these programs.',
  },
];

const OUTLINE_SECTIONS: OutlineSection[] = [
  {
    title: '1. Executive Summary',
    bullets: [
      "Overview of Issaquah's postsecondary readiness landscape",
      'Key equity narrative: progress made, but persistent gaps remain',
    ],
  },
  {
    title: '2. Postsecondary Readiness Overview',
    bullets: [
      'District-wide graduation rates and trends',
      'College enrollment rates by pathway (2-yr, 4-yr, direct)',
      'Career readiness indicators and CTE completion',
    ],
  },
  {
    title: '3. Equity Analysis: Progress & Persistent Gaps',
    bullets: [
      'Disaggregated outcomes by race/ethnicity and income level',
      'Where gaps are narrowing vs. where they persist',
      'Comparison to state averages and peer districts',
    ],
  },
  {
    title: '4. College Readiness Deep Dive',
    bullets: [
      'AP/IB participation and pass rates across student groups',
      'Dual enrollment trends and access equity',
      'SAT/ACT performance and college-going rates',
    ],
  },
  {
    title: '5. CTE Pathways & Career Readiness',
    bullets: [
      'CTE enrollment, completion, and certification data',
      'CTE as a lever for closing equity gaps',
      'Student outcomes by CTE pathway vs. traditional track',
    ],
  },
  {
    title: '6. Spotlight: Gibson EK School',
    bullets: [
      'Non-traditional, project-based learning model overview',
      'Alternative assessment approach (no traditional grading)',
      'Key successes: student engagement and outcomes',
      'Challenges: scaling, data comparability, perceptions',
    ],
  },
  {
    title: '7. Recommendations & Next Steps',
    bullets: [
      'Data-informed action items for district leadership',
      'Priority areas for equity-focused investment',
      'Suggested metrics to track progress year-over-year',
    ],
  },
];

const SECTIONS: SectionMeta[] = [
  {
    id: 'college-eligibility',
    title: 'College Eligibility',
    index: 1,
    description: 'Graduation requirements, eligibility rates, and access to advanced coursework.',
    intro:
      'This section examines college eligibility across the Issaquah School District, analyzing the requirements students must meet and how district-wide statistics reflect readiness for higher education. The data reveals a pattern of overall strong performance with significant equity gaps that persist across demographic groups.',
    status: 'in-progress',
    paragraphs: [
      { id: 'college-eligibility__p1', num: 1, title: 'Graduation Requirements & Eligibility Rates', hasChart: true },
      { id: 'college-eligibility__p2', num: 2, title: 'Advanced Coursework & Dual Enrollment', hasChart: false },
    ],
  },
  {
    id: 'college-readiness',
    title: 'College Readiness',
    index: 2,
    description: "XQ's three-measure framework: coursework, GPA, and assessment performance.",
    intro:
      'College readiness in the Issaquah School District encompasses academic preparation, assessment performance, and access to resources that position students for success in postsecondary education. While the district demonstrates strong overall readiness metrics, disaggregated data reveals meaningful disparities in how well different student populations are prepared for the transition to college.',
    status: 'not-started',
    paragraphs: [
      { id: 'college-readiness__p1', num: 1, title: 'Coursework Intensity & GPA Performance', hasChart: true },
      { id: 'college-readiness__p2', num: 2, title: 'Assessment Performance & College Going Rates', hasChart: false },
    ],
  },
  {
    id: 'cte-pathways',
    title: 'CTE Pathways',
    index: 3,
    description: 'Career & Technical Education enrollment, completion, and equity outcomes.',
    intro:
      'Career & Technical Education (CTE) pathways in Issaquah serve as a critical postsecondary on-ramp, with strong career-prepared completion rates that reach students who may not meet traditional college-ready benchmarks.',
    status: 'not-started',
    paragraphs: [
      { id: 'cte-pathways__p1', num: 1, title: 'CTE Enrollment & Completion', hasChart: true },
      { id: 'cte-pathways__p2', num: 2, title: 'CTE as an Equity Lever', hasChart: false },
    ],
  },
  {
    id: 'equity-analysis',
    title: 'Equity Analysis',
    index: 4,
    description: 'Disaggregated outcomes, gap trends, and peer-district comparisons.',
    intro:
      'This analysis examines how outcomes vary across racial, ethnic, linguistic, and economic subgroups, identifying where Issaquah is closing gaps and where they persist relative to state averages and peer districts.',
    status: 'not-started',
    paragraphs: [
      { id: 'equity-analysis__p1', num: 1, title: 'Disaggregated Outcomes by Subgroup', hasChart: true },
      { id: 'equity-analysis__p2', num: 2, title: 'Trends & Peer District Comparisons', hasChart: false },
    ],
  },
  {
    id: 'gibson-spotlight',
    title: 'Gibson EK Spotlight',
    index: 5,
    description: 'A non-traditional, project-based school: successes and challenges.',
    intro:
      'Gibson EK School operates a non-traditional, project-based learning model with an alternative assessment approach. This spotlight examines what makes the school distinctive and the challenges of measuring its outcomes against district-wide metrics.',
    status: 'not-started',
    paragraphs: [
      { id: 'gibson-spotlight__p1', num: 1, title: 'Project-Based Learning Model', hasChart: false },
      { id: 'gibson-spotlight__p2', num: 2, title: 'Successes & Challenges', hasChart: false },
    ],
  },
  {
    id: 'recommendations',
    title: 'Recommendations',
    index: 6,
    description: 'Data-informed action items and priority equity investments.',
    intro:
      'These recommendations translate the report findings into concrete action items for district leadership, focusing on the student groups and outcomes where targeted investment is most likely to close equity gaps.',
    status: 'not-started',
    paragraphs: [
      { id: 'recommendations__p1', num: 1, title: 'Priority Action Items', hasChart: false },
      { id: 'recommendations__p2', num: 2, title: 'Metrics to Track Year-over-Year', hasChart: false },
    ],
  },
];

const FACTS: Prototype2Facts = {
  district: { name: 'Issaquah School District', cohort: 'Class of 2024', year: '2023-24' },
  cohort_size: 2310,
  postsecondary_readiness: {
    overall_pct: 76,
    by_route: { college_only: 16, career_only: 25, both: 35 },
    lagging_subgroups: { iep: 29, black: 45, hispanic_latino: 51, multilingual_learners: 56 },
  },
  graduation: {
    overall_pct: 94.2,
    by_subgroup: {
      economically_disadvantaged: 81.3,
      non_economically_disadvantaged: 96.8,
      hispanic_latino: 85.7,
      white: 96.1,
    },
    gap_pp: { economic: 15.5, hispanic_white: 10.4 },
  },
  college_eligibility: {
    overall_pct: 54,
    by_subgroup: {
      white: 63,
      hispanic_latino: 34,
      black: 37,
      iep: 19,
      multilingual_learners: 28,
      economically_disadvantaged: 32,
      non_economically_disadvantaged: 61,
    },
  },
  cte: {
    career_prepared_only_pct: 25,
  },
};

const INITIAL_DRAFTS: Record<string, string> = {};

const INITIAL_SECTION_STATUSES: Record<string, SectionStatus> = Object.fromEntries(
  SECTIONS.map((s) => [s.id, s.status])
);

export { SECTIONS, FACTS, NARRATIVE_OPTIONS, OUTLINE_SECTIONS, INITIAL_DRAFTS, INITIAL_SECTION_STATUSES };
export type {
  SectionMeta,
  SectionStatus,
  ParagraphMeta,
  Prototype2Facts,
  NarrativeOption,
  NarrativeOptionId,
  OutlineSection,
};
